import { useEffect } from 'react';
import { useToast } from 'ui/components/Toast';

type NotifyVariant = 'success' | 'error' | 'warning' | 'info';

interface NotifyOptions {
	type?: NotifyVariant | 'default';
	description?: string;
	// Opciones de react-toastify que el design system ignora (se conservan para no tocar cada llamada)
	autoClose?: number | false;
	[key: string]: unknown;
}

type Emit = (title: string, variant: NotifyVariant, description?: string) => void;

let emitter: Emit | null = null;

const send = (variant: NotifyVariant, message: unknown, options?: NotifyOptions) => {
	const title = typeof message === 'string' ? message : String((message as { message?: string })?.message ?? message);
	emitter?.(title, variant, options?.description);
};

/**
 * Notificaciones desde código que no es un componente (servicios, contextos).
 * Mismo uso que `toast` de react-toastify; las muestra el `ToastProvider` del design system
 * a través de `NotifyBridge`.
 */
export const toast = Object.assign(
	(message: unknown, options?: NotifyOptions) =>
		send(options?.type && options.type !== 'default' ? options.type : 'info', message, options),
	{
		success: (message: unknown, options?: NotifyOptions) => send('success', message, options),
		error: (message: unknown, options?: NotifyOptions) => send('error', message, options),
		warning: (message: unknown, options?: NotifyOptions) => send('warning', message, options),
		info: (message: unknown, options?: NotifyOptions) => send('info', message, options),
		/** Avisa al empezar y al resolver; devuelve la misma promesa (como react-toastify). */
		promise: <T,>(
			promise: Promise<T>,
			messages: { pending?: string; success?: string; error?: string },
			options?: NotifyOptions
		): Promise<T> => {
			if (messages.pending) send('info', messages.pending, options);
			promise.then(
				() => messages.success && send('success', messages.success, options),
				() => messages.error && send('error', messages.error, options)
			);
			return promise;
		}
	}
);

/** Conecta `toast` con el `ToastProvider`; se monta una sola vez debajo de él. */
export function NotifyBridge() {
	const { toast: show } = useToast();

	useEffect(() => {
		emitter = (title, variant, description) => show({ title, variant, description });
		return () => {
			emitter = null;
		};
	}, [show]);

	return null;
}
